/**
 * Landing v3 — the FAQ (Figma 38:1581 inside 38:1455): a glass sheet holding
 * ten accordion rows. One row is open at a time and ALL start closed (user
 * 2026-09-24 — the frame showed the first open); the answer's height animates
 * so the rows below slide rather than jump. The sheet rises in when it
 * reaches the screen, then its header and rows follow one after another, like
 * the tiles. Copy lives under `landingV3.faq` in every locale.
 */
import { motion, useInView, useReducedMotion } from 'framer-motion';
import { ChevronDownIcon, MessageCircleQuestionIcon } from 'lucide-react';
import { type ComponentProps, useId, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router';

import { cn } from '@/lib/utils';
import { localizePath, splitLangFromPath } from '@/seo/marketing-routes';
import { LANDING_FAQ_KEYS } from '@/seo/page-content';

import { GLASS_PILL_CLASS } from './landing-chrome';

/** Question keys, in page order — how the memory loop works right after
 *  what Flashka is, Anki beside materials, the price beside the plans (user
 *  2026-09-30), and security last, the way the reference FAQs close. Shared with the crawler copy and the FAQPage data. */
const FAQ_KEYS = LANDING_FAQ_KEYS;

/** Each question and its answer paragraphs, as whole i18n keys. */
const FAQ_COPY: Record<(typeof FAQ_KEYS)[number], { q: string; a: readonly string[] }> = {
  what: {
    q: 'landingV3.faq.items.what.q',
    a: ['landingV3.faq.items.what.a'],
  },
  llm: {
    q: 'landingV3.faq.items.llm.q',
    a: ['landingV3.faq.items.llm.a'],
  },
  memory: {
    q: 'landingV3.faq.items.memory.q',
    a: [
      'landingV3.faq.items.memory.a.0',
      'landingV3.faq.items.memory.a.1',
      'landingV3.faq.items.memory.a.2',
    ],
  },
  materials: {
    q: 'landingV3.faq.items.materials.q',
    a: ['landingV3.faq.items.materials.a'],
  },
  anki: {
    q: 'landingV3.faq.items.anki.q',
    a: ['landingV3.faq.items.anki.a'],
  },
  subjects: {
    q: 'landingV3.faq.items.subjects.q',
    a: ['landingV3.faq.items.subjects.a'],
  },
  plans: {
    q: 'landingV3.faq.items.plans.q',
    a: [
      'landingV3.faq.items.plans.a.0',
      'landingV3.faq.items.plans.a.1',
      'landingV3.faq.items.plans.a.2',
      'landingV3.faq.items.plans.a.3',
    ],
  },
  price: {
    q: 'landingV3.faq.items.price.q',
    a: ['landingV3.faq.items.price.a'],
  },
  progress: {
    q: 'landingV3.faq.items.progress.q',
    a: ['landingV3.faq.items.progress.a'],
  },
  privacy: {
    q: 'landingV3.faq.items.privacy.q',
    a: ['landingV3.faq.items.privacy.a'],
  },
};

/** The Privacy page, in the language of the page the reader is on. */
function PrivacyLink(props: Omit<ComponentProps<typeof Link>, 'to'>) {
  const { pathname } = useLocation();
  return (
    <Link
      {...props}
      to={localizePath('/privacy', splitLangFromPath(pathname).lang)}
      // Privacy opens at its top, not at the FAQ's depth (an SPA navigation
      // keeps the window's scroll).
      onClick={() => window.scrollTo(0, 0)}
      className="underline underline-offset-2 hover:text-secondary-800"
    />
  );
}

/** Inline markup an answer may carry: `<1>` bold (plan names, sub-questions)
 *  and `<2>` the link to the Privacy page. */
const ANSWER_COMPONENTS = {
  1: <b className="font-bold text-secondary-800" />,
  2: <PrivacyLink />,
};

/** The entrance: the sheet, then each row a beat after the one above. */
const EASE = [0.22, 1, 0.36, 1] as const;
const ROW_BEAT = 0.06;

/** An answer is one paragraph, or several (the memory loop, the plans and
 *  limits) — each a whole key; the locales store those as arrays. */
function FaqAnswer({ answerKeys }: { answerKeys: readonly string[] }) {
  return (
    <div className="flex flex-col gap-3 pt-3 text-[16px] font-semibold leading-[22px] text-fg-subtle">
      {answerKeys.map((answerKey) => (
        <p key={answerKey}>
          <Trans i18nKey={answerKey} components={ANSWER_COMPONENTS} />
        </p>
      ))}
    </div>
  );
}

export function FaqSection({ className }: { className?: string }) {
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion();
  const baseId = useId();
  const [open, setOpen] = useState<(typeof FAQ_KEYS)[number] | null>(null);
  const ref = useRef<HTMLElement>(null);
  const arrived = useInView(ref, { once: true, amount: 0.15 });
  const shown = arrived || !!reduceMotion;
  // Each piece's entrance, staggered; opacity and transform only.
  const enter = (i: number) => ({
    initial: reduceMotion ? false : { opacity: 0, y: 20 },
    animate: shown ? { opacity: 1, y: 0 } : undefined,
    transition: { duration: 0.55, delay: 0.15 + i * ROW_BEAT, ease: EASE },
  });

  return (
    <motion.section
      ref={ref}
      aria-labelledby={`${baseId}-title`}
      initial={reduceMotion ? false : { opacity: 0, y: 56, scale: 0.98 }}
      animate={shown ? { opacity: 1, y: 0, scale: 1 } : undefined}
      transition={{ duration: 0.8, ease: EASE }}
      className={cn(
        // Figma 48:21757: radius 64, 40 top and sides, 24 bottom; the top
        // bar's glass so the page's dots show through (user decision).
        'mx-auto flex w-full max-w-[1080px] flex-col items-center gap-6 rounded-[64px] px-10 pb-6 pt-10',
        GLASS_PILL_CLASS,
        className,
      )}>
      {/* Header (Figma 48:21800): icon badge, "Other Questions?", "Check the
          FAQ" — 13px apart, 16px above and below. */}
      <motion.div
        {...enter(0)}
        className="flex flex-col items-center gap-[13px] py-4 text-center">
        <span className="flex items-center justify-center rounded-2xl border border-secondary-400 bg-secondary-100 p-[11px] text-secondary-600 shadow-[0_2px_4px_rgba(0,0,0,0.06)]">
          <MessageCircleQuestionIcon className="h-6 w-6" aria-hidden />
        </span>
        <h2
          id={`${baseId}-title`}
          className="text-[32px] font-bold leading-[38px] text-secondary-600">
          {t('landingV3.faq.titlePre')}
          <span className="text-secondary-800">{t('landingV3.faq.titleHl')}</span>
        </h2>
        <p className="text-[16px] font-semibold leading-[22px] text-fg-subtle">
          {t('landingV3.faq.subtitle')}
        </p>
      </motion.div>
      <div className="flex w-full flex-col">
        {FAQ_KEYS.map((key, i) => {
          const expanded = open === key;
          const panelId = `${baseId}-${key}`;
          return (
            // Figma 48:21760: `secondary-50` at 70%, radius 32, 24px padding —
            // the rows sit flush, their corners overlapping the neighbour.
            <motion.div
              key={key}
              {...enter(i + 1)}
              className="relative rounded-[32px] bg-secondary-50/70 p-6">
              {/* The question is a heading around its button (the ARIA
                  accordion pattern), so the page outline lists every one. */}
              <h3>
                <button
                  type="button"
                  aria-expanded={expanded}
                  aria-controls={panelId}
                  id={`${panelId}-q`}
                  onClick={() => setOpen(expanded ? null : key)}
                  // The WHOLE row is the button (its ::after spans it), not just
                  // the line of text — a thumb lands on the padding as often
                  // as on the words. The open answer sits above it.
                  className="flex w-full items-center justify-between gap-6 text-left after:absolute after:inset-0 after:rounded-[32px] after:content-['']">
                  <span className="text-[20px] font-semibold leading-7 text-secondary-800">
                    {t(FAQ_COPY[key].q)}
                  </span>
                  <motion.span
                    aria-hidden
                    animate={{ rotate: expanded ? 180 : 0 }}
                    transition={reduceMotion ? { duration: 0 } : { duration: 0.2 }}
                    className="flex h-6 w-6 shrink-0 items-center justify-center text-secondary-800">
                    <ChevronDownIcon className="h-6 w-6" />
                  </motion.span>
                </button>
              </h3>
              {/* Every answer is IN the page, closed ones folded to zero height
                  — not unmounted: search engines and AI answers only read what
                  is in the DOM, and every question starts closed. `inert`
                  keeps a folded answer's link out of the tab order. */}
              <motion.div
                id={panelId}
                role="region"
                aria-labelledby={`${panelId}-q`}
                inert={!expanded}
                initial={false}
                animate={
                  expanded ? { height: 'auto', opacity: 1 } : { height: 0, opacity: 0 }
                }
                transition={
                  reduceMotion
                    ? { duration: 0 }
                    : { duration: 0.28, ease: [0.22, 1, 0.36, 1] }
                }
                className="relative z-10 overflow-hidden">
                <FaqAnswer answerKeys={FAQ_COPY[key].a} />
              </motion.div>
            </motion.div>
          );
        })}
      </div>
    </motion.section>
  );
}
